import { RefObject } from "react";

import { isEqual } from "lodash-es";
import { createStore } from "zustand";

import type { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import {
  AmbientContextualEntityRegistrationOptions,
  ContextualEntity,
  ContextualEntityKey,
  contextualEntityKey,
  RefetchCallback,
} from "src/unifiedCopilot/context/types";
import { logger } from "src/utils/logger";

export type AmbientContextualEntityEntry = {
  entity: ContextualEntity;
  /**
   * Number of live registrations currently declaring this key. The entry is
   * removed when the count reaches zero, so an entity declared by two
   * components survives until both have released it.
   */
  registrantCount: number;
  /**
   * Number of live registrations that want this entity sent to the agent
   * automatically. If it reaches zero while registrants remain, a reference entity
   * is still available for mentions.
   */
  agentContextRegistrantCount: number;
};

export type AmbientContextualEntitiesRegistration = {
  put: (entities: ContextualEntity[]) => void;
  dispose: () => void;
};

export type SuggestionPromptsRegistration = {
  put: (prompts: ChatEmptyStatePrompt[]) => void;
  dispose: () => void;
};

type SuggestionPromptsRegistrationId = number;

type CopilotState = {
  /**
   * Route- and component-scoped entities currently present in the UI. This is
   * the only source read when building automatic agent context.
   */
  ambientContextualEntities: Map<
    ContextualEntityKey,
    AmbientContextualEntityEntry
  >;
  /**
   * Empty-state suggestions declared by the mounted surfaces, one list per
   * live registration in registration order. The chat falls back to its
   * defaults while this is empty.
   */
  suggestionPrompts: Map<
    SuggestionPromptsRegistrationId,
    ChatEmptyStatePrompt[]
  >;
};

type CopilotActions = {
  createAmbientContextualEntitiesRegistration: (
    options?: AmbientContextualEntityRegistrationOptions,
  ) => AmbientContextualEntitiesRegistration;
  /**
   * Adds a ref to the refetch set and returns its disposer.
   */
  registerRefetchCallback: (
    callbackRef: RefObject<RefetchCallback>,
  ) => () => void;
  /**
   * Fans out to every registered refetch callback.
   */
  notifyMCPMutation: () => void;
  /**
   * A registration for empty-state suggestion prompts, the surface-declared
   * counterpart of contextual entities: `put` replaces this registration's
   * prompts, `dispose` withdraws them.
   */
  createSuggestionPromptsRegistration: () => SuggestionPromptsRegistration;
};

export type CopilotStore = ZustandStore<CopilotState, CopilotActions>;

type SetAmbientContextualEntities = (
  partial: Pick<CopilotState, "ambientContextualEntities">,
) => void;

const createAmbientContextualEntitiesRegistration = (
  get: () => CopilotStore,
  set: SetAmbientContextualEntities,
  includeInAgentContext: boolean,
): AmbientContextualEntitiesRegistration => {
  const registration = {
    heldKeys: new Set<ContextualEntityKey>(),
    isDisposed: false, // To mark terminal state and prevent further puts after dispose
  };

  const applyPut = (entities: ContextualEntity[]): void => {
    const current = get().ambientContextualEntities;
    // Dedupe within the call: the last entry per key wins.
    const incomingEntities = new Map(
      entities.map((entity): [ContextualEntityKey, ContextualEntity] => [
        contextualEntityKey(entity),
        entity,
      ]),
    );

    // One decision per existing entry: keep it untouched (return existing as is), replace its value,
    // adjust its count, or drop it (null).
    const decide = (
      key: ContextualEntityKey,
      existingEntityInTheState: AmbientContextualEntityEntry,
    ): AmbientContextualEntityEntry | null => {
      const incomingEntity = incomingEntities.get(key);
      const isHeldByThisRegistration = registration.heldKeys.has(key);

      // This put no longer declares the key
      if (incomingEntity === undefined) {
        // Never ours: someone else's entry, not ours to touch
        if (!isHeldByThisRegistration) {
          return existingEntityInTheState;
        }
        // We were the last registrant: the entry is dropped
        if (existingEntityInTheState.registrantCount <= 1) {
          return null;
        }
        // Release our share; other registrants keep the entry alive
        return {
          entity: existingEntityInTheState.entity,
          registrantCount: existingEntityInTheState.registrantCount - 1,
          agentContextRegistrantCount:
            existingEntityInTheState.agentContextRegistrantCount -
            (includeInAgentContext ? 1 : 0),
        };
      }
      // This put declares the key: count us once if we didn't hold it yet
      const registrantCount =
        existingEntityInTheState.registrantCount +
        (isHeldByThisRegistration ? 0 : 1);
      const agentContextRegistrantCount =
        existingEntityInTheState.agentContextRegistrantCount +
        (!isHeldByThisRegistration && includeInAgentContext ? 1 : 0);
      const sameValue = isEqual(
        existingEntityInTheState.entity,
        incomingEntity,
      );
      // Nothing changed: same object out, so no store write and no re-renders
      if (
        sameValue &&
        registrantCount === existingEntityInTheState.registrantCount &&
        agentContextRegistrantCount ===
          existingEntityInTheState.agentContextRegistrantCount
      ) {
        return existingEntityInTheState;
      }
      // Replace the value (last write wins) and/or record the extra registrant
      return {
        entity: sameValue ? existingEntityInTheState.entity : incomingEntity,
        registrantCount,
        agentContextRegistrantCount,
      };
    };

    // For each existing entry, decide whether to keep it, update it, or drop it.
    const kept = [...current.entries()].flatMap(([key, entry]) => {
      const decided = decide(key, entry);
      return decided === null ? [] : [[key, decided] as const];
    });
    // For each incoming entry that wasn't already in the state, add it with a count of 1.
    const added = [...incomingEntities.entries()]
      .filter(([key]) => !current.has(key))
      .map(
        ([key, entity]) =>
          [
            key,
            {
              entity,
              registrantCount: 1,
              agentContextRegistrantCount: includeInAgentContext ? 1 : 0,
            },
          ] as const,
      );

    // Update the held keys
    registration.heldKeys = new Set(incomingEntities.keys());

    const changed =
      added.length > 0 ||
      kept.length !== current.size ||
      kept.some(([key, entry]) => entry !== current.get(key));

    if (!changed) {
      return;
    }

    // If there's any change set the new state
    set({ ambientContextualEntities: new Map([...kept, ...added]) });
  };

  return {
    put: (entities) => {
      if (registration.isDisposed) {
        return;
      }
      applyPut(entities);
    },
    dispose: () => {
      if (registration.isDisposed) {
        return;
      }
      applyPut([]);
      registration.isDisposed = true;
    },
  };
};

type SetSuggestionPrompts = (
  partial: Pick<CopilotState, "suggestionPrompts">,
) => void;

const createSuggestionPromptsRegistration = (
  get: () => CopilotStore,
  set: SetSuggestionPrompts,
  id: SuggestionPromptsRegistrationId,
): SuggestionPromptsRegistration => {
  const registration = { isDisposed: false };

  return {
    put: (prompts) => {
      if (registration.isDisposed) {
        return;
      }
      const current = get().suggestionPrompts;
      // Same prompts again: no store write, no re-renders
      if (isEqual(current.get(id), prompts)) {
        return;
      }
      // A re-put keeps this registration's place in the order
      set({ suggestionPrompts: new Map(current).set(id, prompts) });
    },
    dispose: () => {
      if (registration.isDisposed) {
        return;
      }
      registration.isDisposed = true;
      const current = get().suggestionPrompts;
      if (!current.has(id)) {
        return;
      }
      const next = new Map(current);
      next.delete(id);
      set({ suggestionPrompts: next });
    },
  };
};

const invokeRefetchCallbackSafely = (
  callbackRef: RefObject<RefetchCallback>,
): void => {
  try {
    const result = callbackRef.current();
    if (result instanceof Promise) {
      result.catch((error: unknown) => {
        logger.error("Copilot refetch callback failed", error);
      });
    }
  } catch (error) {
    logger.error("Copilot refetch callback failed", error);
  }
};

export const createCopilotStore = () => {
  // Callbacks are non-serializable and nothing subscribes to the set itself,
  // so it lives outside the reactive state.
  const refetchCallbacks = new Set<RefObject<RefetchCallback>>();
  // Registration ids only need to be unique within this store.
  const suggestionRegistrationIds = { next: 0 };

  return createStore<CopilotStore>((set, get) => ({
    ambientContextualEntities: new Map(),
    suggestionPrompts: new Map(),
    actions: {
      createAmbientContextualEntitiesRegistration: (options = {}) =>
        createAmbientContextualEntitiesRegistration(
          get,
          set,
          options.includeInAgentContext ?? false,
        ),
      registerRefetchCallback: (callbackRef) => {
        refetchCallbacks.add(callbackRef);
        return () => {
          refetchCallbacks.delete(callbackRef);
        };
      },
      notifyMCPMutation: () => {
        [...refetchCallbacks].map(invokeRefetchCallbackSafely);
      },
      createSuggestionPromptsRegistration: () => {
        suggestionRegistrationIds.next += 1;
        return createSuggestionPromptsRegistration(
          get,
          set,
          suggestionRegistrationIds.next,
        );
      },
    },
  }));
};
